// =================================================================
// THE LANDING'S MARK BY DAMAGE TYPE (plan design-gaps, S4)
// =================================================================
// What a hit that got through leaves, drawn by time from the landing
// (`beat.markAt`, living `MARK_MS[mark]`), so a lingering mark outlives the
// landing phase:
//   shock (Impact)   - rings that run out along the floor, and the shove: a
//                      skid on the floor along the blow (drawn, so a plain
//                      hit never moves a figure or runs the panel's frame)
//   burst (Explosive)- a hot disc and its fragments flying out
//   burn (Energy)    - a glow that lingers on the figure and fades
//   tear (Rending)   - a slash that stays open, then knits shut, stitch by stitch
// A screen that holds still shows each in its standing form. The rings and
// the skid lie on the ground, projected through the camera; the timing is the
// engine's (`markShare`, `knitOf`).
import clsx from '../../utils/clsx';
import type { ReactNode } from 'react';
import { SCENE_VIEW, project, type Camera } from '../../engine/battlefield/projection';
import { bearingBetween, headingPoint } from '../../engine/battlefield/facing';
import type { Vec3 } from '../../engine/battlefield/types';
import { knitOf, markShare, type Beat } from '../../engine/display/beats';
import type { ScreenXY } from '../battlefieldTokens/markerGeometry';
import { TAU, onCircle, px } from './looks/place';
import styles from '../../styles/Display.module.css';

type Mark = NonNullable<Beat['mark']>;
type XY = Pick<ScreenXY, 'x' | 'y'>;

const FRAGMENTS = 6;
const STITCHES = 3;
/** Impact's rings on the floor (m): from this radius, out this far over their life. */
const SHOCK_FROM_M = 0.15;
const SHOCK_REACH_M = 0.6;
/** Impact's skid (m) along the blow, drawn over the first third of the mark's life. */
const SKID_M = 0.45;
const RING_SIDES = 20;

interface MarkProps {
  beat: Beat;
  t: number | null;
  /** The struck figure's middle on the screen. */
  to: XY;
  camera: Camera;
}

/** The mark this beat's landing leaves, where it is in its life. */
export default function BeatMark({ beat, t, to, camera }: MarkProps) {
  const k = markShare(beat, t);
  if (k === null || !beat.mark) return null;
  return (
    <g
      data-testid="display-beat-mark"
      data-mark-kind={beat.mark}
      data-knit={beat.mark === 'tear' ? Number(knitOf(k).toFixed(2)) : undefined}
      className={clsx(styles.beatMark, styles[`mark_${beat.mark}`])}
    >
      {DRAW[beat.mark]({ k, to, beat, camera })}
    </g>
  );
}

type MarkDraw = (p: { k: number; to: XY; beat: Beat; camera: Camera }) => ReactNode;

/** A point on the ground (at the floor's height) as the screen sees it. */
export const onGround = (floor: Vec3, at: { x: number; z: number }, camera: Camera) =>
  project({ x: at.x, y: floor.y, z: at.z }, camera, SCENE_VIEW);

/** A circle of radius `m` metres on the floor about its point, projected: an ellipse under a tilted camera. */
export function groundRing(floor: Vec3, m: number, camera: Camera): string {
  return Array.from({ length: RING_SIDES }, (_, i) => {
    const [dx, dz] = onCircle(m, (i / RING_SIDES) * TAU);
    const p = onGround(floor, { x: floor.x + dx, z: floor.z + dz }, camera);
    return `${px(p.x)},${px(p.y)}`;
  }).join(' ');
}

/** Impact: two rings running out along the floor, the second a little behind, and the skid the shove leaves. */
const Shock: MarkDraw = ({ k, beat, camera }) => {
  const { floor, from } = beat;
  const foot = onGround(floor, floor, camera);
  const end = from
    ? onGround(floor, headingPoint(floor, bearingBetween(from, floor), SKID_M * Math.min(1, k * 3)), camera)
    : null;
  return (
    <>
      {[0, 0.25].map((lag) => {
        const a = Math.max(0, k - lag);
        return (
          <polygon
            key={lag}
            points={groundRing(floor, SHOCK_FROM_M + SHOCK_REACH_M * a, camera)}
            opacity={(1 - a) * 0.9}
          />
        );
      })}
      {end && (
        <line
          className={styles.markSkid}
          x1={px(foot.x)}
          y1={px(foot.y)}
          x2={px(end.x)}
          y2={px(end.y)}
          opacity={1 - k}
        />
      )}
    </>
  );
};

/** Explosive: a hot disc swelling and going out, and its fragments flying past it. */
const Burst: MarkDraw = ({ k, to }) => (
  <>
    <circle className={styles.markDisc} cx={to.x} cy={to.y} r={5 + 12 * k} opacity={1 - k} />
    {Array.from({ length: FRAGMENTS }, (_, i) => {
      const a = (i / FRAGMENTS) * TAU + 0.4;
      const r1 = 8 + 26 * k;
      const [x1, y1] = onCircle(r1, a);
      const [x2, y2] = onCircle(r1 + 5, a);
      return (
        <line key={i} x1={px(to.x + x1)} y1={px(to.y + y1)} x2={px(to.x + x2)} y2={px(to.y + y2)} opacity={1 - k} />
      );
    })}
  </>
);

/** Energy: a glow on the figure that lingers, a hot core inside it, both fading out slowly. */
const Burn: MarkDraw = ({ k, to }) => (
  <>
    <circle className={styles.markGlow} cx={to.x} cy={to.y} r={14} opacity={0.55 * (1 - k)} />
    <circle className={styles.markCore} cx={to.x} cy={to.y} r={5} opacity={1 - k} />
  </>
);

/** Rending: a slash across the figure, its two edges apart while it is open, drawn together as it knits. */
const Tear: MarkDraw = ({ k, to }) => {
  const knit = knitOf(k);
  const gap = 4 * (1 - knit);
  const edge = (side: 1 | -1) =>
    [-12, -6, 0, 6, 12].map((x, i) => `${px(to.x + x)},${px(to.y + (i % 2 ? 3 : -3) + side * gap)}`).join(' ');
  const stitches = Math.round(knit * STITCHES);
  return (
    <>
      <polyline points={edge(1)} />
      <polyline points={edge(-1)} />
      {Array.from({ length: stitches }, (_, i) => {
        const x = to.x - 8 + i * 8;
        return <line key={i} className={styles.markStitch} x1={x} y1={to.y - 6} x2={x} y2={to.y + 6} />;
      })}
    </>
  );
};

const DRAW: Record<Mark, MarkDraw> = { shock: Shock, burst: Burst, burn: Burn, tear: Tear };
